/**
 * pgv2 — Optimized Product Gallery Thumbnails
 * File: assets/thumbnail-gallery.js
 *
 * Zero dependencies. ~2 KB minified.
 * Replaces Slick Slider for the thumbnail strip only.
 *
 * API events (dispatched on document):
 *   pgv2:change  →  detail: { mediaId, fullUrl, mediaType }
 *
 * The main image swap is handled via the pgv2:change event so this
 * file stays decoupled from whatever main-image component you use.
 */
// Guard against slickGoTo calls on destroyed/missing Slick instances
/* ── Patch theme.js productVideoGallery scroll crash ─────────────────
 * theme.js binds a scroll listener that calls player.playVideo() and
 * player.pauseVideo() without checking if the player exists.
 * We patch jQuery's .data() to return a safe proxy when 'youtube-player'
 * is requested but undefined.
 * ------------------------------------------------------------------- */
(function patchYoutubePlayerData() {
  var _originalData = jQuery.fn.data;

  jQuery.fn.data = function(key, value) {
    // Only intercept the getter for 'youtube-player'
    if (key === 'youtube-player' && arguments.length === 1) {
      var result = _originalData.apply(this, arguments);
      if (result == null) {
        // Return a safe no-op proxy so .playVideo() / .pauseVideo() etc.
        // never throw, regardless of what theme.js calls on it
        return {
          playVideo:       function() {},
          pauseVideo:      function() {},
          stopVideo:       function() {},
          seekTo:          function() {},
          getCurrentTime:  function() { return 0; },
          getPlayerState:  function() { return -1; },
          _isSafeProxy:    true
        };
      }
      return result;
    }
    return _originalData.apply(this, arguments);
  };
})();
(function () {
  "use strict";

  /* ── Constants ────────────────────────────────────────────────────── */
  const VISIBLE = 3; // thumbnails visible at once
  const GAP_PX = 6; // must match CSS gap (px)

  /* ── Init ─────────────────────────────────────────────────────────── */
  function initGallery(root) {
    const track = root.querySelector("[data-track]");
    const items = Array.from(root.querySelectorAll("[data-thumb-item]"));
    const btnPrev = root.querySelector(".pgv2__arrow--prev");
    const btnNext = root.querySelector(".pgv2__arrow--next");

    if (!track || items.length === 0) return;

    let currentIndex = 0; // index of leftmost visible thumbnail
    let activeIndex = 0; // index of currently selected thumbnail
    const total = items.length;

    /* ── Measure: derive thumb width from the viewport width ────────── */
    function thumbWidth() {
      const vp = root.querySelector(".pgv2__viewport");
      return (vp.clientWidth - GAP_PX * (VISIBLE - 1)) / VISIBLE;
    }

    function stepSize() {
      return thumbWidth() + GAP_PX;
    }

    /* ── Translate track to currentIndex ────────────────────────────── */
    function applyTransform(animated) {
      if (!animated) track.style.transition = "none";
      track.style.transform = `translateX(-${currentIndex * stepSize()}px)`;
      if (!animated) {
        // Force reflow, then re-enable transitions
        track.getBoundingClientRect();
        track.style.transition = "";
      }
    }

    /* ── Update arrow disabled states ───────────────────────────────── */
    function updateArrows() {
      if (total <= VISIBLE) return;
      const maxIndex = total - VISIBLE;
      btnPrev.disabled = currentIndex <= 0;
      btnNext.disabled = currentIndex >= maxIndex;
    }

    /* ── Activate a thumbnail ────────────────────────────────────────── */
    function activateItem(index, scrollIntoView) {
      items[activeIndex].classList.remove("pgv2__item--active");
      items[activeIndex].setAttribute("aria-pressed", "false");

      activeIndex = index;
      items[activeIndex].classList.add("pgv2__item--active");
      items[activeIndex].setAttribute("aria-pressed", "true");

      /* Auto-scroll track so the active item is visible */
      if (scrollIntoView) {
        const maxIndex = Math.max(0, total - VISIBLE);
        if (index < currentIndex) {
          currentIndex = index;
        } else if (index >= currentIndex + VISIBLE) {
          currentIndex = Math.min(index - VISIBLE + 1, maxIndex);
        }
        applyTransform(true);
        updateArrows();
      }

      /* Fire decoupled event for the main image component */
      const item = items[index];
      document.dispatchEvent(
        new CustomEvent("pgv2:change", {
          bubbles: true,
          detail: {
            mediaId: item.dataset.mediaId,
            fullUrl: item.dataset.fullUrl,
            mediaType: item.dataset.mediaType,
          },
        }),
      );
    }

    /* ── Arrow clicks ────────────────────────────────────────────────── */
    btnPrev &&
      btnPrev.addEventListener("click", function () {
        if (currentIndex > 0) {
          currentIndex--;
          applyTransform(true);
          updateArrows();
        }
      });

    btnNext &&
      btnNext.addEventListener("click", function () {
        const maxIndex = Math.max(0, total - VISIBLE);
        if (currentIndex < maxIndex) {
          currentIndex++;
          applyTransform(true);
          updateArrows();
        }
      });

    /* ── Thumbnail clicks + keyboard ─────────────────────────────────── */
    items.forEach(function (item, idx) {
      item.addEventListener("click", function () {
        activateItem(idx, true);
      });

      item.addEventListener("keydown", function (e) {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          activateItem(idx, true);
        }
        /* Arrow key navigation within the strip */
        if (e.key === "ArrowRight") {
          e.preventDefault();
          const next = Math.min(idx + 1, total - 1);
          items[next].focus();
          activateItem(next, true);
        }
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          const prev = Math.max(idx - 1, 0);
          items[prev].focus();
          activateItem(prev, true);
        }
      });
    });

    /* ── Lazy-load fade-in via IntersectionObserver ──────────────────── */
    if ("IntersectionObserver" in window) {
      const imgObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              const img = entry.target;
              img.classList.add("pgv2__img--loaded");
              imgObserver.unobserve(img);
            }
          });
        },
        { rootMargin: "40px" },
      );

      items.forEach(function (item) {
        const img = item.querySelector(".pgv2__img");
        if (img) imgObserver.observe(img);
      });
    } else {
      /* Fallback: show all images immediately */
      items.forEach(function (item) {
        const img = item.querySelector(".pgv2__img");
        if (img) img.classList.add("pgv2__img--loaded");
      });
    }

    /* ── Touch / swipe support ───────────────────────────────────────── */
    let touchStartX = 0;
    let touchStartY = 0;

    root.addEventListener(
      "touchstart",
      function (e) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      },
      { passive: true },
    );

    root.addEventListener(
      "touchend",
      function (e) {
        const dx = e.changedTouches[0].clientX - touchStartX;
        const dy = e.changedTouches[0].clientY - touchStartY;
        /* Only count horizontal swipes that are not primarily vertical */
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          const maxIndex = Math.max(0, total - VISIBLE);
          if (dx < 0 && currentIndex < maxIndex) {
            currentIndex++;
          } else if (dx > 0 && currentIndex > 0) {
            currentIndex--;
          }
          applyTransform(true);
          updateArrows();
        }
      },
      { passive: true },
    );

    /* ── Recalculate on resize (debounced) ───────────────────────────── */
    var resizeTimer;
    window.addEventListener("resize", function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        requestAnimationFrame(function () {
          applyTransform(false);
          updateArrows();
        });
      }, 120);
    });

    /* ── Initial paint ───────────────────────────────────────────────── */
    applyTransform(false);
    updateArrows();
  }

  /* ── Bootstrap: run after DOM is ready ──────────────────────────────── */
  function bootstrap() {
    document.querySelectorAll("[data-thumbnail-gallery]").forEach(initGallery);

    // Preload all main slide images via <link rel="preload"> — reliable across browsers
    document.querySelectorAll("[data-main-image]").forEach(function (img) {
      if (!img.src) return;
      var link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = img.src;
      if (img.srcset) {
        link.imageSrcset = img.srcset;
        link.imageSizes = img.sizes || "100vw";
      }
      document.head.appendChild(link);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootstrap);
  } else {
    bootstrap();
  }

  /* ── Shopify Section Events (theme editor live-reload) ───────────────── */
  document.addEventListener("shopify:section:load", function (e) {
    var gallery = e.target.querySelector("[data-thumbnail-gallery]");
    if (gallery) initGallery(gallery);
  });

  /* ================================================================
   * MAIN IMAGE INTEGRATION
   * ================================================================ */
  document.addEventListener("pgv2:change", function (e) {
    var detail = e.detail;
    var mediaType = detail.mediaType;

    // Strip the section-id prefix: "template--xxx__main-31182536310838" → "31182536310838"
    var numericId = detail.mediaId.split("-").pop();

    /* ── FIX: Stop & fully reset any currently playing video ────────── */
    function stopAllVideos() {
      var allSlides = document.querySelectorAll(
        "[data-image-gallery-main] .productView-image",
      );
      allSlides.forEach(function (slide) {
        // Native <video> — pause, rewind, then call load() to fully reset
        var video = slide.querySelector("video");
        if (video) {
          video.pause();
          video.currentTime = 0;
          video.load(); // ← KEY FIX: fully resets the element so next play() starts from 0
        }

        // YouTube — post message to iframe
// AFTER
var ytIframe = slide.querySelector('iframe[src*="youtube"]');
if (ytIframe && ytIframe.contentWindow) {
  try {
    ytIframe.contentWindow.postMessage(
      JSON.stringify({ event: "command", func: "stopVideo", args: [] }), "*"
    );
  } catch (err) {
    // iframe not accessible — silently ignore
  }
}

        // Vimeo — post message to iframe
        var vimeoIframe = slide.querySelector('iframe[src*="vimeo"]');
        if (vimeoIframe) {
          vimeoIframe.contentWindow.postMessage(
            JSON.stringify({ method: "pause" }),
            "*",
          );
          vimeoIframe.contentWindow.postMessage(
            JSON.stringify({ method: "setCurrentTime", value: 0 }),
            "*",
          );
        }
      });
    }

    /* ── For images: swap src directly ──────────────────────────────── */
    if (mediaType === "image") {
      stopAllVideos();
      var allSlides = document.querySelectorAll(
        "[data-image-gallery-main] .productView-image",
      );

      allSlides.forEach(function (slide) {
        var container = slide.querySelector("[data-media-id]");
        if (!container) return;

        var isTarget = container.getAttribute("data-media-id") === numericId;

        if (isTarget) {
          slide.style.opacity = "1";
          slide.style.zIndex = "999";
          slide.classList.add("slick-current", "slick-active");
          slide.setAttribute("aria-hidden", "false");

          var img = container.querySelector("[data-main-image]");
          if (img) {
            img.onload = null;
            img.onerror = null;

            if (img.complete && img.naturalWidth > 0) {
              img.style.transition = "none";
              img.style.opacity = "1";
            } else {
              img.style.opacity = "0.01";
              img.style.transition = "opacity 0.2s ease";
              img.onload = function () {
                img.style.opacity = "1";
              };
              img.onerror = function () {
                img.style.opacity = "1";
              };
            }
          }
        } else {
          slide.style.opacity = "0";
          slide.style.zIndex = "998";
          slide.classList.remove("slick-current", "slick-active");
          slide.setAttribute("aria-hidden", "true");
        }
      });

      return;
    }

    /* ── For video / external_video ─────────────────────────────────── */
    if (mediaType === "video" || mediaType === "external_video") {
      stopAllVideos();
      var allSlides = document.querySelectorAll(
        "[data-image-gallery-main] .productView-image",
      );

      var targetIndex = -1;
      allSlides.forEach(function (slide, i) {
        var container = slide.querySelector("[data-media-id]");
        if (
          container &&
          container.getAttribute("data-media-id") === numericId
        ) {
          targetIndex = i;
        }
      });

      if (targetIndex === -1) return;

      /* Promote the video slide visually */
      allSlides.forEach(function (slide, i) {
        if (i === targetIndex) {
          slide.style.opacity = "1";
          slide.style.zIndex = "999";
          slide.classList.add("slick-current", "slick-active");
          slide.setAttribute("aria-hidden", "false");
        } else {
          slide.style.opacity = "0";
          slide.style.zIndex = "998";
          slide.classList.remove("slick-current", "slick-active");
          slide.setAttribute("aria-hidden", "true");
        }
      });

      /* FIX: Wait for video to be ready after load() reset, then play */
      setTimeout(function () {
        requestAnimationFrame(function () {
          var targetSlide = allSlides[targetIndex];
          if (!targetSlide) return;

          var video = targetSlide.querySelector("video");
          if (video) {
            video.style.display = "block";

            if (video.readyState >= 2) {
              // Enough data available — play immediately
              video.play().catch(function () {});
            } else {
              // Wait for canplay event after load() reset
              video.addEventListener("canplay", function onCanPlay() {
                video.removeEventListener("canplay", onCanPlay);
                video.play().catch(function () {});
              });
            }
            return;
          }

// AFTER
var ytDiv = targetSlide.querySelector("[data-youtube]");
if (ytDiv) {
  var videoId = ytDiv.getAttribute("data-youtube");
  // Find the YT player instance the theme stores — common patterns:
  var ytPlayer =
    (window.YTPlayers && window.YTPlayers[videoId]) ||   // some themes
    (window.ytPlayers && window.ytPlayers[videoId]) ||   // variation
    ytDiv._ytPlayer ||                                    // stored on element
    null;

  if (ytPlayer && typeof ytPlayer.playVideo === "function") {
    // Player ready — call directly, no click needed
    try {
      ytPlayer.seekTo(0);
      ytPlayer.playVideo();
    } catch (err) {
      // Fallback to click if direct call fails
      ytDiv.dispatchEvent(new Event("click", { bubbles: true }));
    }
  } else {
    // Player not ready yet — wait for it then play
    var ytPollCount = 0;
    var ytPoll = setInterval(function () {
      ytPollCount++;
      var p =
        (window.YTPlayers && window.YTPlayers[videoId]) ||
        (window.ytPlayers && window.ytPlayers[videoId]) ||
        ytDiv._ytPlayer ||
        null;

      if (p && typeof p.playVideo === "function") {
        clearInterval(ytPoll);
        try {
          p.seekTo(0);
          p.playVideo();
        } catch (err) {
          ytDiv.dispatchEvent(new Event("click", { bubbles: true }));
        }
      } else if (ytPollCount > 20) {
        // Give up after ~2s, fallback to click
        clearInterval(ytPoll);
        ytDiv.dispatchEvent(new Event("click", { bubbles: true }));
      }
    }, 100);
  }
  return;
}

          var vimeoDiv = targetSlide.querySelector("[data-vimeo]");
          if (vimeoDiv) {
            vimeoDiv.dispatchEvent(new Event("click", { bubbles: true }));
          }
        });
      }, 150);
    }
  });
})();